/**
 * How a box looks on the canvas — extracted from `ImageCanvas.svelte` so it can be unit-tested
 * and rendered in the `/styles` gallery without a second, hand-built copy of the rules drifting
 * away from the one the annotator actually sees.
 *
 * ---------------------------------------------------------------------------
 * Colour is never the only signal
 * ---------------------------------------------------------------------------
 * `annotator-api-frontend.md` requires it, and a tool that fails that requirement is unusable
 * for a colour-blind annotator and useless in a greyscale printout of a screenshot. So every
 * distinction below is carried three ways: a stroke colour, a dash pattern, and a text badge.
 * The badge is the fallback of last resort — if the dashes cannot separate at some zoom (see
 * the arithmetic under `DASH_UNIT`), the badge still can. `/styles` renders all three at
 * 0.25x / 1x / 8x with a greyscale toggle so that claim can be checked rather than asserted.
 *
 * ---------------------------------------------------------------------------
 * No Konva in this file
 * ---------------------------------------------------------------------------
 * The attribute bags below are plain data. Keeping Konva out means these rules are testable in
 * the plain-node test project, and it keeps the module honest: it decides *what* a box looks
 * like, never *how* it is drawn.
 */

import type { DraftBox } from './draft';

/**
 * The part of a box that determines its appearance.
 *
 * Narrower than `DraftBox` on purpose: a style rule that could read `bbox` would be a style
 * rule that could disagree with the geometry, and `DraftBox` is assignable to this anyway.
 */
export type StyledBox = Pick<
  DraftBox,
  'source' | 'alignment' | 'uncertain' | 'restored' | 'mirrored' | 'display' | 'letterClass'
>;

/**
 * The five appearances a box can have. This is a *presentation* discriminator only.
 *
 * `uncertain` and `restored` remain flags on the box and are never classes — see
 * `annotation-data-model.md`. A box can be both; `boxKind` picks one appearance for it, and the
 * badge still shows both marks, which is why the badge and not the border is the authoritative
 * readout.
 */
export type BoxKind = 'expected' | 'uncertain' | 'restored' | 'unexpected' | 'model';

export const BOX_KINDS: readonly BoxKind[] = [
  'expected',
  'uncertain',
  'restored',
  'unexpected',
  'model',
] as const;

export const BOX_KIND_LABEL: Record<BoxKind, string> = {
  expected: 'expected letter',
  uncertain: 'uncertain {}',
  restored: 'restored []',
  unexpected: 'unexpected insertion',
  model: 'model-proposed',
};

/**
 * Which appearance a box takes.
 *
 * Order is load-bearing and matches the original if-chain in `ImageCanvas.svelte`: provenance
 * first (a model proposal must never be mistaken for a human box, whatever else is true of it),
 * then the alignment disagreement, then `restored`, then `uncertain`.
 */
export function boxKind(box: StyledBox): BoxKind {
  if (box.source !== 'human') return 'model';
  if (box.alignment === 'unexpected') return 'unexpected';
  if (box.restored) return 'restored';
  if (box.uncertain) return 'uncertain';
  return 'expected';
}

export const STROKE_BY_KIND: Record<BoxKind, string> = {
  expected: '#6ea8ff',
  uncertain: '#e0b341',
  restored: '#b98cf0',
  unexpected: '#f0705f',
  model: '#58c6d6',
};

export function strokeFor(box: StyledBox): string {
  return STROKE_BY_KIND[boxKind(box)];
}

/**
 * Dash patterns, in **screen pixels**.
 *
 * `strokeScaleEnabled: false` makes Konva stroke outside the stage transform, so both
 * `strokeWidth` and `dash` are read in screen px. The unit used to be derived from the viewport
 * scale (`3 / scale`), i.e. image units — at a fit scale of 0.25 that is a 24-px dash on a box
 * only a few dozen screen px wide, which renders as a solid line and collapses every category
 * into "same border". A constant screen-space unit next to a constant `strokeWidth: 2` is the
 * pattern that holds at every zoom.
 *
 * It does **not** hold at every box *size*, and that is the limitation `/styles` exists to make
 * visible. A 30x30 image-pixel glyph at a 0.25 fit scale is 7.5 screen px on a side — 30 px of
 * perimeter — against a model-proposal dash period of 39 px. One period does not fit. At that
 * combination the border is not a pattern, it is a fragment, and only the badge separates the
 * categories. Look at it before trusting it.
 */
export const DASH_UNIT = 3;

export const DASH_BY_KIND: Record<BoxKind, number[] | undefined> = {
  expected: undefined,
  uncertain: [DASH_UNIT * 4, DASH_UNIT * 2],
  restored: [DASH_UNIT, DASH_UNIT * 2],
  unexpected: [DASH_UNIT * 2, DASH_UNIT * 2],
  model: [DASH_UNIT * 6, DASH_UNIT * 3, DASH_UNIT, DASH_UNIT * 3],
};

export function dashFor(box: StyledBox): number[] | undefined {
  return DASH_BY_KIND[boxKind(box)];
}

/** Screen pixels a dash pattern takes to repeat. Compare against a box's screen perimeter. */
export function dashPeriod(kind: BoxKind): number {
  return (DASH_BY_KIND[kind] ?? []).reduce((total, segment) => total + segment, 0);
}

/**
 * The text badge. The signal that survives greyscale, colour blindness, a tiny box and a dash
 * period wider than the box's perimeter.
 */
export function badgeFor(box: StyledBox): string {
  const marks: string[] = [];
  if (box.alignment === 'unexpected') marks.push('+');
  if (box.uncertain) marks.push('?');
  if (box.restored) marks.push('[ ]');
  if (box.mirrored) marks.push('⇄');
  if (box.source !== 'human') marks.push('M');
  return marks.join(' ');
}

export function labelFor(box: StyledBox): string {
  const badge = badgeFor(box);
  const letter = box.display ?? `#${box.letterClass}`;
  return badge ? `${letter} ${badge}` : letter;
}

// ---------------------------------------------------------------------------
// Konva attribute bags (plain data — the caller owns the node)
// ---------------------------------------------------------------------------

export const BOX_STROKE_WIDTH = 2;
export const BOX_FILL = 'rgba(110, 168, 255, 0.06)';
/** Screen px, above the box's top-left corner. */
export const LABEL_OFFSET = 16;
/** Screen px. */
export const LABEL_FONT_SIZE = 13;
export const LABEL_FONT_FAMILY =
  'Charis SIL, Gentium Plus, Doulos SIL, Noto Serif, DejaVu Sans, serif';

export interface BoxRectAttrs {
  stroke: string;
  dash: number[];
  dashEnabled: boolean;
  strokeWidth: number;
  /** Screen-space stroke: a 2px border stays 2px at 8x, which is what makes a tight box judgeable. */
  strokeScaleEnabled: false;
  fill: string;
}

export function boxRectAttrs(box: StyledBox): BoxRectAttrs {
  const dash = dashFor(box);
  return {
    stroke: strokeFor(box),
    dash: dash ?? [],
    dashEnabled: dash !== undefined,
    strokeWidth: BOX_STROKE_WIDTH,
    strokeScaleEnabled: false,
    fill: BOX_FILL,
  };
}

export interface BoxLabelAttrs {
  text: string;
  fontSize: number;
  fontFamily: string;
  fill: string;
}

/**
 * `unit` is `1 / viewport.scale` — image units per screen pixel. The label is drawn in the
 * boxes layer, which is in image space, so a screen-constant 13px glyph is `13 * unit` there.
 * Same arithmetic for {@link LABEL_OFFSET}, which the caller applies to `y`.
 */
export function boxLabelAttrs(box: StyledBox, unit: number): BoxLabelAttrs {
  return {
    text: labelFor(box),
    fontSize: LABEL_FONT_SIZE * unit,
    fontFamily: LABEL_FONT_FAMILY,
    fill: strokeFor(box),
  };
}
